import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState, useCallback } from "react";
import { TableData } from "@/App/Distributions/SellersPending/Table";
import { useAuth } from "@/hooks/auth";
import { getAllDistributionsProcessBySellerId } from "@/Api/distributions";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";

type ProductClassificationType = {
  id: number;
  name: string;
  quota_quantity: number;
};

type ContestType = {
  id: number;
  name: string;
  validation_date: string;
  prize: number;
  contest_product_balance: {
    id: number;
    uuid: string;
    contest_id: number;
    cp_classification_id: number;
    balance: number;
    with_held_balance: number;
  };
};

type ContestProductClassificationType = {
  id: number;
  product_name: string;
  product_classification_id: number;
  contest_id: number;
  lottery_id: number;
  product_classification: ProductClassificationType;
  contest: ContestType;
  lottery: {
    id: number;
    name: string;
    color: string;
  };
};

type CreatedByType = {
  id: number;
  name: string;
};

type UpdatedByType = {
  id: number;
  name: string;
};

type UnityType = {
  id: number;
  name: string;
};

type SellerType = {
  id: number;
  uuid: string;
  phone: string;
  document: string;
  user_id: number;
  user: {
    id: number;
    uuid: string;
    name: string;
    email: string;
  };
};

type DistributionFileType = {
  id: number;
  name: string | null;
  file: string;
  file_name: string;
  url: string;
  created_at: string;
};

type DistributionProcessType = {
  id: number;
  uuid: string;
  cp_classification_id: number;
  from_unity_id: number | null;
  unity_id: number;
  quota_quantity: number;
  from_seller_id: number | null;
  seller_id: number;
  distribution_type_id: number;
  distribution_process_status_id: number;
  created_by: CreatedByType;
  updated_by: UpdatedByType | null;
  created_at: string;
  updated_at: string;
  processed_at: string | null;
  seller: SellerType;
  contest_product_classification: ContestProductClassificationType;
  unity: UnityType;
  distribution_type: {
    id: number;
    name: string;
  };
  distribution_process_status: {
    id: number;
    name: string;
  };
  files?: DistributionFileType[];
};

type GroupedDistributionsType = {
  contests: {
    id: string;
    name: string | null;
    lottery: string;
    products: {
      id: number;
      name: string;
      classification: string;
      quotas: number;
      quota_quantity_utilized: number;
      distribution_process_uuid: string;
      distribution_process_status_id: number;
      distribution_process_status_name: string;
      seller_name: string;
      seller_email: string;
      created_by_name: string;
      updated_by_name: string | null;
      created_at: string;
      updated_at: string;
      files?: DistributionFileType[];
    }[];
  }[];
};

type StatusFilter = "all" | "pending" | "accepted" | "rejected";

export const PageList = () => {
  const [state, setState] = useState<"loading" | "production" | "error">(
    "loading"
  );
  const [allData, setAllData] = useState<DistributionProcessType[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedDistributionsType>({
    contests: [],
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const { hasPermission } = usePermissions();
  const user = useAuth()?.user;
  const sellerId = user?.seller?.id;

  const filterDataByStatus = useCallback((data: DistributionProcessType[], filter: StatusFilter) => {
    let filtered = data;

    if (filter === "pending") {
      filtered = data.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.PENDING);
    } else if (filter === "accepted") {
      filtered = data.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED);
    } else if (filter === "rejected") {
      filtered = data.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.REJECTED);
    }

    const contestsMap = new Map();

    filtered.forEach((item: DistributionProcessType) => {
      if (!item.contest_product_classification.contest) {
        return;
      }
      const contestId = item.contest_product_classification.contest_id.toString();

      if (!contestsMap.has(contestId)) {
        contestsMap.set(contestId, {
          id: contestId,
          name: item.contest_product_classification.contest.name ?? null,
          lottery: item.contest_product_classification.lottery.name,
          lottery_color: item.contest_product_classification?.lottery?.color || "transparent",
          lottery_id: item.contest_product_classification.lottery_id,
          products: [],
        });
      }

      const contest = contestsMap.get(contestId);

      contest.products.push({
        id: item.id,
        name: item.contest_product_classification.product_name,
        classification: item.contest_product_classification.product_classification.name,
        quotas: item.quota_quantity,
        distribution_type_id: item.distribution_type_id,
        quota_quantity_utilized: 0,
        distribution_process_uuid: item.uuid,
        distribution_process_status_id: item.distribution_process_status_id,
        distribution_process_status_name: item.distribution_process_status.name,
        seller_name: item?.seller?.user?.name,
        seller_email: item?.seller?.user?.email,
        created_by_name: item?.created_by?.name,
        updated_by: item.updated_by?.name || null,
        created_at: item.created_at,
        updated_at: item.processed_at || item.updated_at,
        files: item.files || [],
      });
    });

    setGroupedData({
      contests: Array.from(contestsMap.values()),
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!sellerId) return;

    try {
      setState("loading");
      const { data } = await getAllDistributionsProcessBySellerId(
        sellerId.toString()
      );

      const distributions = data?.response?.data || [];
      setAllData(distributions);

      setState("production");
    } catch (error) {
      console.error(error);
      setState("error");
    }
  }, [sellerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    filterDataByStatus(allData, statusFilter);
  }, [statusFilter, allData, filterDataByStatus]);

  const pageActions = {
    update: () => {
      fetchData();
    },
  };

  const getStatusCounts = () => {
    const pending = allData.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.PENDING).length;
    const accepted = allData.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED).length;
    const rejected = allData.filter(item => item.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.REJECTED).length;
    return { pending, accepted, rejected, all: allData.length };
  };

  const counts = getStatusCounts();

  return (
    <Main>
      <If condition={hasPermission("view-distributions-list")}>
        <div className="flex flex-col gap-4">
          <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
            <h2 className="text-2xl font-semibold flex gap-2 items-center">
              Minhas Distribuições
            </h2>
          </div>

          <Tabs value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)} className="w-full">
            <TabsList className="grid w-full grid-cols-4 max-w-xl">
              <TabsTrigger value="all" className="flex gap-2">
                Todas
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs">
                  {counts.all}
                </span>
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex gap-2">
                Pendentes
                {counts.pending > 0 && (
                  <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 px-2 py-0.5 rounded-full text-xs">
                    {counts.pending}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="accepted" className="flex gap-2">
                Aceitas
                {counts.accepted > 0 && (
                  <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-0.5 rounded-full text-xs">
                    {counts.accepted}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="rejected" className="flex gap-2">
                Rejeitadas
                {counts.rejected > 0 && (
                  <span className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 px-2 py-0.5 rounded-full text-xs">
                    {counts.rejected}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-col gap-3">
            {state === "loading" ? (
              <CreateLoaderTable cols={4} lines={6} />
            ) : (
              <TableData groupedData={groupedData} pageActions={pageActions} />
            )}
          </div>
        </div>
      </If>
    </Main>
  );
};
